/**
 * Breadcrumbs Component - Uses shadcn/ui Breadcrumb
 */
import React, { memo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';

interface BreadcrumbItemDef {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items?: Array<string | BreadcrumbItemDef>;
  onBack?: () => void;
  backPath?: string;
}

const Breadcrumbs = memo(({
  items,
  onBack,
  backPath = '/dashboard'
}: BreadcrumbsProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(backPath);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 mb-4">
      <Button
        variant="ghost"
        size="icon"
        onClick={handleBack}
        aria-label="Go back"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>
      <Breadcrumb>
        <BreadcrumbList>
          {items.map((item: string | BreadcrumbItemDef, index: number) => {
            const isLast = index === items.length - 1;
            return (
              <React.Fragment key={index}>
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage>
                      {typeof item === 'object' ? item.label : item}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={typeof item === 'object' ? item.href : '#'}>
                        {typeof item === 'object' ? item.label : item}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
});

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
